import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { getMapImageUrl } from '../../utils/formatters';
import RankBadge from '../elo/RankBadge';
import SteamAvatar from '../common/SteamAvatar';
import TournamentBanner from '../tournaments/TournamentBanner';
import NewsBanner from '../news/NewsBanner';
import OpenSlotsBanner from '../openslots/OpenSlotsBanner';
import SupportBox from '../donations/SupportBox';
import useCanSuggestMap from '../../hooks/useCanSuggestMap';
import './Dashboard.css';

/**
 * Main landing page showing community stats, recent matches, ELO top 5,
 * latest skin drop, and most popular map.
 */
export default function Dashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { eligible: canSuggestMap } = useCanSuggestMap();

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard`)
      .then(r => r.json())
      .then(async (d) => {
        // Fetch avatars for ELO top players
        const steamIds = (d.elo_top || []).map(p => p.steamid64).filter(Boolean);
        if (steamIds.length > 0) {
          await fetchAvatarsBulk(steamIds);
        }
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="dashboard"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  if (!data) {
    return <div className="dashboard"><p>{t('dashboard.loadError')}</p></div>;
  }

  const { community, recent_matches, elo_top, latest_drop, season } = data;

  return (
    <div className="dashboard">
      {season && (
        <div className="dash-season-badge" onClick={() => navigate('/stats/season')}>
          {season.name}
        </div>
      )}

      {/* Live backfill: jump into a 5v5 that's short a player (registered, active only) */}
      <OpenSlotsBanner />

      {/* Latest pinned news banner */}
      <NewsBanner />

      {/* Active tournaments banner */}
      <TournamentBanner />

      {canSuggestMap && (
        <button
          type="button"
          className="dash-suggest-banner"
          onClick={() => navigate('/suggest-map')}
        >
          <span className="dash-suggest-icon">🗺️</span>
          <span className="dash-suggest-text">
            <strong>{t('nav.suggestMap')}</strong>
            <small>{t('dashboard.suggestMapDesc')}</small>
          </span>
          <span className="dash-suggest-arrow">→</span>
        </button>
      )}

      {/* Community stats */}
      <div className="dash-stats-row">
        <div className="dash-stat-card lift-card notched-card stagger-in" style={{ '--i': 0 }}>
          <span className="dash-stat-value hero-numeral">{community.total_matches}</span>
          <span className="dash-stat-label">{t('dashboard.matchesPlayed')}</span>
        </div>
        <div className="dash-stat-card lift-card notched-card stagger-in" style={{ '--i': 1 }}>
          <span className="dash-stat-value hero-numeral">{community.total_players}</span>
          <span className="dash-stat-label">{t('nav.players')}</span>
        </div>
        <div className="dash-stat-card lift-card notched-card stagger-in" style={{ '--i': 2 }}>
          <span className="dash-stat-value hero-numeral">{(community.total_kills || 0).toLocaleString()}</span>
          <span className="dash-stat-label">{t('dashboard.totalKills')}</span>
        </div>
        <div className="dash-stat-card lift-card notched-card stagger-in" style={{ '--i': 3 }}>
          <span className="dash-stat-value hero-numeral">{community.matches_this_week}</span>
          <span className="dash-stat-label">{t('dashboard.thisWeek')}</span>
        </div>
      </div>

      <div className="dash-grid">
        {/* Recent matches */}
        <div className="dash-card lift-card">
          <div className="dash-card-header">
            <h2 className="section-heading">{t('dashboard.latestMatches')}</h2>
            <button className="dash-link" onClick={() => navigate('/stats/matches')}>{t('dashboard.allMatches')}</button>
          </div>
          <div className="dash-matches">
            {recent_matches.map(m => (
              <button
                key={m.matchid}
                className="dash-match-row"
                onClick={() => navigate(`/stats/matches/${m.matchid}`)}
              >
                <span className="dash-match-map">{m.map_name?.replace('de_', '') || m.map_tag?.replace('de_', '')}</span>
                <span className="dash-match-teams">
                  <span className={m.winner === m.team1_name ? 'winner' : ''}>{m.team1_name}</span>
                  <span className="dash-match-score">{m.team1_score}–{m.team2_score}</span>
                  <span className={m.winner === m.team2_name ? 'winner' : ''}>{m.team2_name}</span>
                </span>
                {m.start_time && (
                  <span className="dash-match-time">
                    {new Date(m.start_time).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ELO top 5 */}
        <div className="dash-card lift-card">
          <div className="dash-card-header">
            <h2 className="section-heading">{t('dashboard.eloRanking')}</h2>
            <button className="dash-link" onClick={() => navigate('/stats/leaderboard')}>{t('dashboard.fullLeaderboard')}</button>
          </div>
          <div className="dash-elo-list">
            {elo_top.map((p, i) => (
              <button
                key={p.steamid64 || p.discord_user_id || i}
                className="dash-elo-row"
                onClick={() => p.steamid64 && navigate(`/stats/players/${p.steamid64}`)}
              >
                <span className="dash-elo-pos">#{i + 1}</span>
                <SteamAvatar steamid64={p.steamid64} size={28} />
                <span className="dash-elo-name">{p.player_name}</span>
                <RankBadge elo={p.elo} rank={p.rank} size="small" />
                <span className="dash-elo-value">{p.elo}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Latest drop */}
        {latest_drop && latest_drop.prize && (() => {
          const prize = latest_drop.prize;
          const rarityColors = {
            consumer: '#B0C3D9',
            industrial: '#5E98D9',
            mil_spec: '#4B69FF',
            restricted: '#8847FF',
            classified: '#D32CE6',
            covert: '#EB4B4B',
            contraband: '#E4AE39',
          };
          const rarityColor = rarityColors[prize.rarity] || '#4B69FF';
          const clickable = !!latest_drop.match;
          return (
            <div
              className={`dash-card dash-card-drop lift-card${clickable ? ' dash-card-drop-clickable' : ''}`}
              style={{ borderColor: rarityColor }}
              onClick={clickable ? () => navigate(`/stats/matches/${latest_drop.match}`) : undefined}
              role={clickable ? 'button' : undefined}
            >
              <div
                className="dash-drop-glow"
                style={{ background: `radial-gradient(circle at 30% 50%, ${rarityColor}22 0%, transparent 60%)` }}
              />
              <div className="dash-card-header">
                <h2 className="section-heading">🎁 {t('dashboard.latestDrop')}</h2>
              </div>
              <div className="dash-drop">
                {prize.image_url && (
                  <img
                    src={prize.image_url}
                    alt={prize.display_name || prize.name}
                    className="dash-drop-img"
                    style={{ filter: `drop-shadow(0 0 10px ${rarityColor}99)` }}
                  />
                )}
                <div className="dash-drop-info">
                  <span className="dash-drop-name">
                    {prize.stattrak && <span className="dash-drop-stattrak">StatTrak™ </span>}
                    {prize.name}
                  </span>
                  <span className="dash-drop-meta" style={{ color: rarityColor }}>
                    {[prize.condition_display, prize.rarity_display].filter(Boolean).join(' · ')}
                  </span>
                  {prize.estimated_value_sek && (
                    <span className="dash-drop-value">~{prize.estimated_value_sek} SEK</span>
                  )}
                  <span className="dash-drop-winner">
                    🏆 {latest_drop.winner_name || t('dashboard.unknown')}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Support */}
        <SupportBox compact />

        {/* Favorite map */}
        {community.favorite_map && (
          <div
            className="dash-card dash-card-map lift-card"
            style={community.favorite_map.image_url ? {
              backgroundImage: `url(${getMapImageUrl(community.favorite_map.image_url)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            } : undefined}
          >
            <div className="dash-fav-map-overlay">
              <h2 className="dash-fav-map-title">{t('dashboard.popularMap')}</h2>
              <span className="dash-fav-map-name">{community.favorite_map.name}</span>
              <span className="dash-fav-map-count">{community.favorite_map.times_played} matcher</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
